import {
  ChartSpline,
  Home,
  Package,
  Settings,
  ShoppingCart,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";

/**
 * Single source of truth for the application navigation tree.
 *
 * Consumed by:
 *  - components/body/NavMenu.jsx        (classic sidebar)
 *  - layout/TopNav/TopMenu.jsx          (modern top menu)
 *  - pages/Home/ModernHome              (launchpad tiles)
 *
 * Returns an array of groups:
 *   { key, title, icon, path?, children?: [{ title, path }] }
 *
 * A group with `path` and no `children` is a direct link.
 * Access/feature gating is identical to the original NavMenu.jsx.
 *
 * Module order & terminology follow mainstream ERP convention
 * (Odoo / Dynamics 365 BC / Zoho):
 *   Home → Sales → Purchasing → Inventory → Accounting → CRM → Reports → Employees → Settings
 */
const iconCls = "text-[22px] text-slate-600";

export const buildMenu = (access = {}, settings = {}, t = (k) => k) => {
  const groups = [];

  // ----- Home -----
  if (access.dashboardAccess) {
    groups.push({
      key: "home",
      title: t("home"),
      path: "home",
      icon: <Home className={iconCls} />,
    });
  }

  // Everything below requires an active fiscal year (same as classic menu).
  // Exception: a freshly-provisioned org has fyAccess but no fiscal year yet —
  // the /fiscal_year route is intentionally available pre-FY (accountsRoutes.js
  // registers it before its own fiscalYearId guard), so surface just that item
  // here; otherwise onboarding is a dead-end (only Home, no way to create the
  // first fiscal year from the menu).
  if (!settings?.fiscalYearId) {
    if (access?.fyAccess) {
      groups.push({
        key: "accounts",
        title: t("accounts"),
        icon: <Wallet className={iconCls} />,
        children: [{ title: t("fiscalYear"), path: "fiscal_year" }],
      });
    }
    return groups;
  }

  // Generic Services module (any business type) — gates Services, Service
  // Categories, and Service Discount Types. Must match catalogRoutes.js /
  // setupRoutes.js or the menu item shows but 404s.
  const servicesOn = !!settings?.enableServices;
  // Car-Service vertical — gates Service Bays and Vehicles (Vehicle Brands,
  // customer vehicles). Stays on BusinessType, separate from the Services switch.
  const isCarService = settings?.businessType === 1;

  // ----- Sales -----
  if (
    access.posAccess ||
    access.cashSalesInvoiceAccess ||
    access.salesHistoryAccess ||
    access.customerAccess ||
    access.customerGroupAccess ||
    access.couponAccess ||
    access.wastageAccess
  ) {
    const children = [
      ...(access.posAccess
        ? [
            { title: t("pos"), path: "pos" },
            ...(access.pos?.registerClosing
              ? [{ title: t("closeRegister"), path: "close_reg" }]
              : []),
            ...(access.pos?.quote
              ? [{ title: t("quotationSales"), path: "quotes" }]
              : []),
          ]
        : []),
      ...(access.cashSalesInvoiceAccess
        ? [{ title: t("cashSalesInvoice"), path: "cash_sales_invoice" }]
        : []),
      ...(access.salesHistoryAccess
        ? [{ title: t("salesHistory"), path: "sales_history" }]
        : []),
      ...(access.pos?.returnSale
        ? [{ title: t("salesReturns"), path: "sale_returns" }]
        : []),
      ...(access.wastageAccess
        ? [{ title: t("stockWriteOff"), path: "stock_write_off" }]
        : []),
      ...(access.customerAccess
        ? [{ title: t("customers"), path: "customers" }]
        : []),
      ...(access.customerGroupAccess
        ? [{ title: t("customersGroup"), path: "customer_groups" }]
        : []),
      ...(access.couponAccess
        ? [{ title: t("coupons", "Coupons"), path: "coupons" }]
        : []),
    ];
    groups.push({
      key: "sales",
      title: t("sales"),
      icon: <TrendingUp className={iconCls} />,
      children,
    });
  }

  // ----- Purchasing -----
  // Three independent documents, each always available (no mode toggle): the store owner
  // uses whichever they need. Purchase Order (commitment) → Goods Receipt (stock in → GRNI)
  // → Purchase Invoice (the bill → creates the payable; standalone or clearing a receipt).
  // The create forms are routed under stock.create — gate the same way or a view-only user 404s.
  if (
    access.supplierAccess ||
    access.stockAccess ||
    access.goodsReceiptAccess ||
    access.supplierBillAccess ||
    access.stockReturnAccess
  ) {
    const children = [
      ...(access.supplierAccess ? [{ title: t("suppliers"), path: "suppliers" }] : []),
      // Purchase Order register (the list). Creating a new PO is the "New" button inside the
      // list header — no separate create-form menu item (merged, same as Goods Receipts).
      ...(access.stockAccess
        ? [{ title: t("purchaseOrder"), path: "purchase_orders" }]
        : []),
      // The supplier bill — creates the payable. Standalone (one-step: stock in + bill) or
      // clears a Goods Receipt. This is what most simple clients use on its own.
      ...(access.supplierBillAccess
        ? [{ title: t("purchaseInvoices", "Purchase Invoices"), path: "purchase_invoices" }]
        : []),
      // Goods Receipt register — every posted GRN (PO-linked + standalone).
      ...(access.goodsReceiptAccess
        ? [{ title: t("goodsReceipts", "Goods Receipts"), path: "goods_receipts" }]
        : []),
      ...(access.stockReturnAccess
        ? [{ title: t("returnStock"), path: "purchase_returns" }]
        : []),
    ];
    groups.push({
      key: "purchase",
      title: t("purchase"),
      icon: <ShoppingCart className={iconCls} />,
      children,
    });
  }

  // ----- Inventory -----
  if (
    access.printLabelAccess ||
    access.catalogAccess ||
    access.categoryAccess ||
    access.brandAccess ||
    access.tagAccess ||
    access.productAttributeAccess ||
    access.inventoryCountAccess ||
    access.inventoryAdjustmentAccess ||
    (servicesOn && (access.serviceAccess || access.serviceCategoryAccess))
  ) {
    const children = [
      ...(access.catalogAccess ? [{ title: t("products"), path: "products" }] : []),
      // (Purchases register moved to the Purchasing section.)
      ...(access.catalogAccess
        ? [{ title: t("stockTransfer"), path: "stock_transfer_history" }]
        : []),
      ...(access?.inventoryCountAccess
        ? [{ title: t("inventoryCounts"), path: "inventory_counts" }]
        : []),
      ...(access?.inventoryAdjustmentAccess
        ? [{ title: t("inventoryAdjustments"), path: "inventory_adjustments" }]
        : []),
      ...(access.printLabelAccess
        ? [{ title: t("printLabels"), path: "print_label_history" }]
        : []),
      ...(access.categoryAccess
        ? [{ title: t("category"), path: "product_category" }]
        : []),
      ...(access.brandAccess ? [{ title: t("brands"), path: "brands" }] : []),
      ...(access.tagAccess ? [{ title: t("tags"), path: "tags" }] : []),
      ...(access.productAttributeAccess
        ? [{ title: t("productAttributes"), path: "product_attributes" }]
        : []),
      ...(access.catalogAccess
        ? [{ title: t("prodPriceType"), path: "price_categories" }]
        : []),
      ...(access.catalogAccess
        ? [{ title: t("unitManagement"), path: "unit_management" }]
        : []),
      ...(servicesOn && access.serviceAccess
        ? [{ title: t("services"), path: "services" }]
        : []),
      ...(servicesOn && access.serviceCategoryAccess
        ? [{ title: t("serviceCategories"), path: "service_categories" }]
        : []),
    ];
    groups.push({
      key: "inventory",
      title: t("inventory"),
      icon: <Package className={iconCls} />,
      children,
    });
  }

  // ----- Accounting -----
  if (
    access?.journalVoucherAccess ||
    access?.expenseAccess ||
    access?.cashMgmtAccess ||
    access?.reportAccess ||
    access?.coaAccess ||
    access?.accountMappingAccess ||
    access?.bankReconciliationAccess ||
    access?.accountTransferAccess ||
    access?.bankAccountAccess ||
    access?.cashBankDashboardAccess ||
    access?.fyAccess
  ) {
    const children = [
      // Transactions
      ...(access?.journalVoucherAccess
        ? [{ title: t("journalVoucher"), path: "journal_voucher" }]
        : []),
      ...(access?.expenseAccess
        ? [{ title: t("expenses"), path: "expenses" }]
        : []),
      ...(access?.cashMgmtAccess
        ? [{ title: t("cashManagement"), path: "cash_management" }]
        : []),
      // Banking / treasury — each on its own grant. These used to ride on
      // `cashMgmtAccess` (and `coaAccess`), which meant letting a cashier drop
      // cash into the till also showed them every bank account and transfer.
      ...(access?.cashBankDashboardAccess
        ? [{ title: t("cashAndBankOverview"), path: "cash_bank_dashboard" }]
        : []),
      ...(access?.accountTransferAccess
        ? [{ title: t("accountTransfers"), path: "account_transfers" }]
        : []),
      ...(access?.bankAccountAccess
        ? [{ title: t("bankAccounts"), path: "bank_accounts" }]
        : []),
      ...(access?.bankReconciliationAccess
        ? [{ title: t("bankReconciliation"), path: "bank_reconciliation" }]
        : []),
      // Ledgers & financial statements
      ...(access?.coaAccess
        ? [{ title: t("coaManagement"), path: "coa_management" }]
        : []),
      ...(access?.accountMappingAccess
        ? [{ title: t("accountMapping", "Account Mapping"), path: "account_mapping" }]
        : []),
      ...(access?.reportAccess
        ? [
            ...(access.report?.profitAndLoss
              ? [
                  { title: t("generalLedger"), path: "general_ledger" },
                  { title: t("journalEntries"), path: "journal_entries" },
                ]
              : []),
            ...(access.report?.trialBalance
              ? [{ title: t("trialBalance"), path: "trial_balance" }]
              : []),
            ...(access.report?.balanceSheet
              ? [{ title: t("balanceSheet"), path: "balance_sheet" }]
              : []),
            ...(access.report?.profitAndLoss
              ? [
                  { title: t("profitAndLoss"), path: "profit_and_loss" },
                  { title: t("cashFlow"), path: "cash_flow" },
                ]
              : []),
            ...(access.report?.supplierAging
              ? [{ title: t("supplierAging", "Supplier Aging"), path: "supplier_aging" }]
              : []),
            ...(access.report?.customerAging
              ? [{ title: t("customerAging", "Customer Aging"), path: "customer_aging" }]
              : []),
          ]
        : []),
      // Period
      ...(access?.fyAccess
        ? [{ title: t("fiscalYear"), path: "fiscal_year" }]
        : []),
    ];
    groups.push({
      key: "accounts",
      title: t("accounts"),
      icon: <Wallet className={iconCls} />,
      children,
    });
  }

  // ----- CRM -----
  if (
    access.inqueryAccess ||
    access.followUpTypeAccess ||
    access.inquerySourceAccess
  ) {
    const children = [
      ...(access.inqueryAccess ? [{ title: t("inquiries"), path: "inquiries" }] : []),
      ...(access.inquerySourceAccess
        ? [{ title: t("inquerySources"), path: "inquery_sources" }]
        : []),
      ...(access.followUpTypeAccess
        ? [{ title: t("followUpTypes"), path: "fullow_up_types" }]
        : []),
    ];
    groups.push({
      key: "crm",
      title: t("crm"),
      icon: <Users className={iconCls} />,
      children,
    });
  }

  // ----- Reports -----
  if (access.reportAccess) {
    const children = [
      ...(access.report?.businessActivity
        ? [{ title: t("businessActivity", "Business Activity"), path: "business_activity" }]
        : []),
      // Sales Reports (/sales_reports) retired 2026-07-28 — it rendered Cost of
      // Goods Sold, Gross Profit and Margin % behind bare `reportAccess`, with no
      // per-report flag of its own. Use Sales Summary / Profit by Invoice instead,
      // which are individually grantable. The API is closed too (SalesReportController).
      { title: t("salesSummary", "Sales Summary"), path: "sales_summary" },
      ...(access.report?.itemWiseSales
        ? [{ title: t("itemWiseSales"), path: "item_wise_sales" }]
        : []),
      ...(access.report?.productMovement
        ? [{ title: t("productPerform"), path: "product_perform_report" }]
        : []),
      ...(access.report?.profitByInvoice
        ? [{ title: t("profitByInvoice"), path: "profit_by_invoice" }]
        : []),
      ...(access.report?.customerWiseProfitLoss
        ? [{ title: t("customerWisePL"), path: "customer_wise_pl" }]
        : []),
      ...(access.report?.creditCustomer
        ? [{ title: t("creditCustomer"), path: "credit_customer_report" }]
        : []),
      ...(access.report?.expenseReport
        ? [{ title: t("expenseReport"), path: "expense_report" }]
        : []),
      { title: t("paymentReports"), path: "payment_reports" },
      { title: t("taxReports"), path: "tax_reports" },
      ...(access.report?.stockReport
        ? [{ title: t("stockReport", "Stock Report"), path: "stock_report" }]
        : []),
      ...(access.report?.productMovement
        ? [{ title: t("stockMovements"), path: "stock_movements_report" }]
        : []),
      ...(access.report?.inventoryAdjustmentReport
        ? [
            {
              title: t("inventoryAdjustmentReport", "Inventory Adjustment Report"),
              path: "inventory_adjustment_report",
            },
          ]
        : []),
      { title: t("registerClosures"), path: "register_closures" },
      { title: t("userReports"), path: "user_reports" },
      ...(access.commissionAccessNav?.report
        ? [{ title: t("commissionReport"), path: "commission_report" }]
        : []),
      ...(access.report?.logReport
        ? [{ title: t("logReport", "Log Report"), path: "log_report" }]
        : []),
    ];
    groups.push({
      key: "reports",
      title: t("reports"),
      icon: <ChartSpline className={iconCls} />,
      children,
    });
  }

  // ----- Employees -----
  if (access.employeeAccess || access.commissionAccessNav?.setup) {
    const children = [
      ...(access.employeeAccess ? [{ title: t("employees"), path: "employees" }] : []),
      ...(access.commissionAccessNav?.setup
        ? [{ title: t("commissionSetup"), path: "commission_setup" }]
        : []),
    ];
    groups.push({
      key: "employees",
      title: t("employees"),
      icon: <UserCog className={iconCls} />,
      children,
    });
  }

  // ----- Settings -----
  if (
    access.generalAccess ||
    access.status ||
    Number(settings?.userType) === 2 ||
    access.outletAccess ||
    access.companyAccess ||
    access.paymentTypeAccess ||
    access.currencyAccess ||
    access.taxRateAccess ||
    access.cityAccess ||
    (isCarService && access.vehicleBrandAccess) ||
    (isCarService && access.serviceBayAccess) ||
    (servicesOn && access.serviceDiscountTypeAccess) ||
    access.userAccess
  ) {
    const children = [
      ...(access.generalAccess ? [{ title: t("orgInfo"), path: "org_info" }] : []),
      ...(access.companyAccess ? [{ title: t("company"), path: "companies" }] : []),
      ...(access.outletAccess ? [{ title: t("outlets"), path: "outlets" }] : []),
      ...(access.paymentTypeAccess
        ? [{ title: t("paymentTypes"), path: "payment_types" }]
        : []),
      ...(access.currencyAccess ? [{ title: t("currency"), path: "currencies" }] : []),
      ...(access.taxRateAccess ? [{ title: t("taxes"), path: "taxes" }] : []),
      ...(access.cityAccess ? [{ title: t("cities"), path: "cities" }] : []),
      ...(isCarService && access.vehicleBrandAccess
        ? [{ title: t("vehicleBrands"), path: "vehicle_brands" }]
        : []),
      ...(isCarService && access.serviceBayAccess
        ? [{ title: t("serviceBays"), path: "service_bays" }]
        : []),
      ...(servicesOn && access.serviceDiscountTypeAccess
        ? [{ title: t("serviceDiscountTypes", "Service Discount Types"), path: "service_discount_types" }]
        : []),
      ...(access.userAccess ? [{ title: t("users"), path: "users" }] : []),
      // Roles & Permissions (the access-profile catalogs) is a platform-owner tool —
      // only the System-Admin manages it, regardless of the userRoleAccess flag.
      ...(Number(settings?.userType) === 2
        ? [{ title: t("rolesPermissions", "Roles & Permissions"), path: "roles_permissions" }]
        : []),
      ...(access.status ? [{ title: t("status"), path: "status" }] : []),
      ...(access.companyAccess
        ? [{ title: t("syncInventory"), path: "sync_inventory" }]
        : []),
    ];
    groups.push({
      key: "settings",
      title: t("settings"),
      icon: <Settings className={iconCls} />,
      children,
    });
  }

  return groups;
};

// The user's default landing route = the first navigable destination in their
// own menu (which already reflects their permissions, in priority order).
// Returns null when the user has no accessible module at all. Single source of
// truth — never hardcode "/home" for landing/redirects.
export const getDefaultRoute = (access = {}, settings = {}) => {
  const menu = buildMenu(access, settings);
  for (const group of menu) {
    if (group.path) return `/${group.path}`;
    const child = group.children?.find((c) => c.path);
    if (child) return `/${child.path}`;
  }
  return null;
};

export default buildMenu;
